import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Info, MapPin } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  PREMIUM_TIERS,
  TRAVEL_MODES,
  TRAVEL_RATES,
  estimateTravelCost,
  type TravelMode,
} from "@/lib/fsap-data";
import { trackCTA } from "@/lib/tracking";

const usd = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/**
 * Transparent travel-cost estimator for FSAP retainers. Uses the same
 * published rates as the written travel policy. Estimate only — never a quote.
 */
export function TravelEstimator() {
  const [mode, setMode] = useState<TravelMode>("drive");
  const [miles, setMiles] = useState(120);
  const [days, setDays] = useState(1);
  const [tierId, setTierId] = useState<string>(PREMIUM_TIERS[1]?.id ?? "priority");

  const { lines, total } = useMemo(
    () => estimateTravelCost({ mode, oneWayMiles: miles, days, tierId }),
    [mode, miles, days, tierId],
  );

  const selectClass =
    "mt-2 w-full appearance-none border border-fg-muted/20 bg-bg-primary px-3 py-2.5 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-gold/60 [&>option]:bg-bg-primary [&>option]:text-fg-primary";

  return (
    <div className="border border-fg-muted/15 bg-bg-secondary/40">
      <div className="border-b border-fg-muted/15 p-6 md:p-8">
        <p className="font-display uppercase tracking-[0.24em] text-gold text-xs flex items-center gap-2">
          <Calculator className="h-3.5 w-3.5" aria-hidden /> Travel estimator
        </p>
        <h3 className="font-display text-2xl md:text-3xl mt-3">
          Estimate travel costs before you book a trip
        </h3>
        <p className="text-fg-secondary text-sm mt-3 max-w-2xl">
          Travel is billed at documented cost on top of your monthly retainer. Enter
          the location and trip length to see a good-faith estimate using our
          published rates.
        </p>
      </div>

      <div className="grid gap-8 p-6 md:grid-cols-2 md:p-8">
        {/* Inputs */}
        <div className="space-y-5">
          <fieldset>
            <legend className="text-sm font-medium">Where is the engagement?</legend>
            <div className="mt-3 space-y-2">
              {TRAVEL_MODES.map((m) => (
                <label
                  key={m.id}
                  className={`flex cursor-pointer items-start gap-3 border p-3 transition-colors ${
                    mode === m.id
                      ? "border-gold bg-gold/[0.06]"
                      : "border-fg-muted/15 hover:border-fg-muted/30"
                  }`}
                >
                  <input
                    type="radio"
                    name="travel_mode"
                    value={m.id}
                    checked={mode === m.id}
                    onChange={() => setMode(m.id)}
                    className="mt-1 accent-[hsl(var(--gold))]"
                  />
                  <span>
                    <span className="block text-sm font-medium">{m.label}</span>
                    <span className="block text-xs text-fg-secondary">{m.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {mode === "drive" && (
            <div>
              <Label htmlFor="travel_miles">One-way distance from San Antonio (miles)</Label>
              <Input
                id="travel_miles"
                type="number"
                inputMode="numeric"
                min={0}
                max={1200}
                step={10}
                value={miles}
                onChange={(e) => setMiles(Math.max(0, Math.min(1200, Number(e.target.value) || 0)))}
                className="mt-2"
              />
              <p className="mt-1 text-xs text-fg-secondary">
                First {TRAVEL_RATES.includedMiles} round-trip miles are included.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="travel_days">Trip length (days on the ground)</Label>
            <Input
              id="travel_days"
              type="number"
              inputMode="numeric"
              min={1}
              max={14}
              step={1}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(14, Number(e.target.value) || 1)))}
              className="mt-2"
            />
            <p className="mt-1 text-xs text-fg-secondary">
              1 day = same-day trip, no lodging. Lodging applies to each overnight.
            </p>
          </div>

          <div>
            <Label htmlFor="travel_tier">Retainer tier</Label>
            <select
              id="travel_tier"
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              className={selectClass}
            >
              {PREMIUM_TIERS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Output */}
        <div className="border border-fg-muted/15 bg-bg-primary/60 p-5 md:p-6">
          <p className="font-display uppercase tracking-[0.24em] text-gold text-[10px]">
            Estimated travel cost
          </p>

          {mode === "local" ? (
            <div className="mt-4">
              <div className="font-display text-4xl">{usd(0)}</div>
              <p className="mt-3 flex items-start gap-2 text-sm text-fg-secondary">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
                Engagements inside the San Antonio metro carry no travel billing — local
                meetings are covered by your retainer.
              </p>
            </div>
          ) : (
            <>
              <div className="mt-3 font-display text-4xl" aria-live="polite">
                {usd(total)}
              </div>
              <p className="text-xs text-fg-secondary mt-1">
                per trip, in addition to the monthly retainer
              </p>
              <dl className="mt-5 space-y-3 border-t border-fg-muted/15 pt-4">
                {lines.map((l) => (
                  <div key={l.label} className="flex items-start justify-between gap-4">
                    <dt className="text-sm">
                      {l.label}
                      <span className="block text-xs text-fg-secondary">{l.detail}</span>
                    </dt>
                    <dd className="text-sm tabular-nums whitespace-nowrap">{usd(l.amount)}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}

          <p className="mt-6 flex items-start gap-2 border-t border-fg-muted/15 pt-4 text-xs leading-relaxed text-fg-secondary">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" aria-hidden />
            Estimate only — not a quote or an offer. Airfare, lodging, and ground
            transport are billed at documented cost with receipts, and every trip is
            quoted and approved in writing before it is booked.
          </p>

          <Link
            to="/advocacy/intake"
            onClick={() => trackCTA("travel_estimator_intake", { destination: "/advocacy/intake", mode, days })}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-gold hover:underline"
          >
            Request a written travel quote <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}
